import { useMemo } from 'react';
import assets from './config/assets';
import env from './config/env';
import { RhinoConfigComponents } from './config/components';
import { merge } from 'lodash-es';

// The project supplies rhino.config at its root; the typecheck configs point
// at a fixture standing in for one.
import rhinoConfig from 'rhino.config';

export type * from './config/components';

export interface RhinoConfig {
  /**
   * Version number of the config file format
   * @default 1.0
   */
  version: number;

  /**
   * The app name, displayed in places like the title bar
   * @default 'RhinoProject'
   */
  appName: string;

  /**
   * The path relative to assets for the dark logo
   * @default 'images/logo-dark.svg'
   */
  darkLogo: string;

  /**
   * The path relative to assets for the light logo
   * @default 'images/logo-light.svg'
   */
  lightLogo: string;

  /**
   * The path relative to assets for the auth pages logo. It is one image in
   * both themes, so it has to read on a light and a dark surface; when empty,
   * the auth pages use the themed logo (darkLogo/lightLogo).
   * @default ''
   */
  authLogo: string;

  /**
   * All assets available under assets.  Use `useRhinoAsset` to access the urls
   */
  assets: typeof assets;

  /**
   * All available environment variables
   */
  env: typeof env;

  /**
   * Configure global, model and attribute components
   */
  components: RhinoConfigComponents;
}

/**
 * The shape of an app's rhino.config file — a partial RhinoConfig,
 * merged over the defaults at load time. Annotate the config with
 * `satisfies RhinoConfigInput` to get component, model, and attribute
 * names checked.
 */
export type RhinoConfigInput = Partial<RhinoConfig>;

export const defaultConfig: RhinoConfig = {
  version: 1,
  appName: 'RhinoProject',
  darkLogo: 'images/logo-dark.svg',
  lightLogo: 'images/logo-light.svg',
  authLogo: '',

  assets,
  env,
  components: {}
};

/**
 * Retrieves the Rhino configuration.
 * * @returns {RhinoConfig} The Rhino configuration object.
 */
export const getRhinoConfig = (): RhinoConfig => {
  // A project states only what it changes; defaultConfig supplies the rest.
  const mergedConfig = merge(
    {},
    defaultConfig,
    rhinoConfig as Partial<RhinoConfig>
  );

  return mergedConfig;
};

/**
 * Hook to retrieve the Rhino configuration.
 * @returns {RhinoConfig} The Rhino configuration object.
 */
export const useRhinoConfig = (): RhinoConfig =>
  useMemo(() => getRhinoConfig(), []);

/**
 * Retrieves the url of a Rhino asset.
 * @param asset - The name of the asset relative to `assets`.
 * @returns The path of the asset, or undefined if the asset is not found.
 */
export const useRhinoAsset = (asset: string): string | undefined => {
  const { assets } = useRhinoConfig();
  const assetPath = `/assets/${asset}`;

  return assets[assetPath]?.default || assets[`/src${assetPath}`]?.default;
};
